import { useEffect } from "react"
import { createPortal } from "react-dom"
import { FiAlertCircle, FiCheck, FiX } from "react-icons/fi"
import Spinner from "./Spinner"

type StatusToastProps = {
  type: "success" | "error" | "loading"
  message: string
  // A loading toast has no close button, the page replaces it with the result
  onClose?: () => void
}

export default function StatusToast({
  type,
  message,
  onClose
}: StatusToastProps) {
  const isSuccess = type === "success"
  const isLoading = type === "loading"

  useEffect(() => {
    if (type !== "success" || !onClose) return

    const timeout = window.setTimeout(onClose, 5000)
    return () => window.clearTimeout(timeout)
  }, [onClose, type])

  const toast = (
    <div
      role={isSuccess || isLoading ? "status" : "alert"}
      style={{ zIndex: 2147483647 }}
      className="fixed right-6 top-6 flex w-[min(24rem,calc(100vw-3rem))] items-start gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow-lg"
    >
      <span
        className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
          isLoading
            ? "bg-gray-100 text-gray-700"
            : isSuccess
              ? "bg-green-50 text-green-700"
              : "bg-orange-50 text-orange-700"
        }`}
      >
        {isLoading ? (
          <Spinner size={16} />
        ) : isSuccess ? (
          <FiCheck className="h-4 w-4" />
        ) : (
          <FiAlertCircle className="h-4 w-4" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-gray-900">
          {isLoading
            ? "Please wait"
            : isSuccess
              ? "Saved"
              : "Action could not be completed"}
        </p>
        <p className="mt-0.5 leading-5">{message}</p>
      </div>

      {!isLoading && (
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center text-gray-400 hover:text-gray-700"
          aria-label="Close notification"
        >
          <FiX className="h-4 w-4" />
        </button>
      )}
    </div>
  )

  return createPortal(toast, document.body)
}
